import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import ChurchFlowLogo from "@/components/ChurchFlowLogo";
import { toast } from "sonner";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refreshOrgs } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/lookup/${token}`);
        setInvite(data);
      } catch (e) {
        setError(formatApiError(e));
      } finally { setLoading(false); }
    })();
  }, [token]);

  const accept = async () => {
    if (!form.name.trim() || form.password.length < 6) {
      return toast.error("Name and a password of 6+ characters are required");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invites/accept`, { token, name: form.name.trim(), password: form.password });
      localStorage.setItem("ledger_token", data.token);
      localStorage.setItem("active_org_id", data.organization_id);
      await refreshOrgs();
      toast.success(`Welcome to ${invite.organization_name}`);
      // counters go directly to mobile portal
      nav(data.role === "counter" ? "/m" : "/dashboard");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md rounded-3xl border-border shadow-none p-8">
        <div className="flex items-center gap-3 mb-6">
          <ChurchFlowLogo size={40} className="soft-glow rounded-2xl" />
          <div className="font-serif text-2xl">ChurchFlow</div>
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading invite…</div>
        ) : error ? (
          <div className="space-y-4">
            <div className="text-destructive font-medium">{error}</div>
            <p className="text-sm text-muted-foreground">Please ask the inviter to send you a new invite link.</p>
            <Link to="/login" className="text-primary text-sm hover:underline">Back to sign in</Link>
          </div>
        ) : (
          <div className="space-y-5" data-testid="accept-invite-form">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">You've been invited</div>
              <h1 className="font-serif text-3xl mt-1.5 leading-tight">Join {invite.organization_name}</h1>
              <p className="text-sm text-muted-foreground mt-2">
                {invite.invited_by_name ? `${invite.invited_by_name} invited you` : "You were invited"} as <Badge variant="outline" className="ml-1">{invite.role}</Badge>
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Your email</Label>
              <Input value={invite.email} disabled className="rounded-xl bg-muted" />
            </div>
            <div className="space-y-1.5">
              <Label>Your name <span className="text-destructive">*</span></Label>
              <Input data-testid="accept-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Create a password <span className="text-destructive">*</span></Label>
              <Input data-testid="accept-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="rounded-xl" />
            </div>

            <Button onClick={accept} disabled={submitting} className="w-full h-11 rounded-xl soft-glow" data-testid="accept-submit-button">
              {submitting ? "Joining…" : `Join ${invite.organization_name}`}
            </Button>
            <div className="text-xs text-center text-muted-foreground">
              Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in instead</Link>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
